import noticiaService from "@/service/noticiaService";
import { usePagination } from "@/composables/usePagination";
import { ref } from "vue";

export function useNoticias() {
  const noticias = ref([]);
  const noticia = ref(null);
  const destaques = ref([]);
  const abrangencias = ref([]);
  const abrangencia = ref(null);
  const autores = ref([]);
  const autor = ref(null);
  const statusList = ref([]);
  const statusItem = ref(null);

  const isLoading = ref(false);
  const isSaving = ref(false);

  const pagination = usePagination({
    initialPage: 0,
    pageSize: 10,
    baseIndex: 0,
  });

  const fetchNoticiasHome = async (
    page = pagination.currentPage.value,
    size = 5
  ) => {
    isLoading.value = true;
    try {
      const response = await noticiaService.getNoticiasHome(page, size, true);
      if (response) {
        noticias.value = response.noticias || [];
        pagination.setPaginationInfo(response);
        pagination.currentPage.value = page;
      }
      return response;
    } catch (error) {
      console.error("Erro ao listar notícias da home:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchAllNoticias = async (
    page = pagination.currentPage.value,
    size = pagination.pageSize.value
  ) => {
    isLoading.value = true;
    try {
      const response = await noticiaService.getAllNoticias(page, size, true);
      if (response) {
        noticias.value = response.noticias || [];
        pagination.setPaginationInfo(response);
        pagination.currentPage.value = page;
      }
      return response;
    } catch (error) {
      console.error("Erro ao listar todas as notícias:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchNoticiasAcao = async (
    page = pagination.currentPage.value,
    size = pagination.pageSize.value,
    acaoId
  ) => {
    isLoading.value = true;
    try {
      const response = await noticiaService.getNoticiasAcao(
        page,
        size,
        acaoId,
        true
      );
      if (response) {
        noticias.value = response.noticias || [];
        pagination.setPaginationInfo(response);
        pagination.currentPage.value = page;
      }
      return response;
    } catch (error) {
      console.error("Erro ao listar notícias por ação:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchNoticiaById = async (id) => {
    isLoading.value = true;
    try {
      const response = await noticiaService.getNoticiaById(id);
      noticia.value = response;
      return response;
    } catch (error) {
      console.error("Erro ao buscar notícia por ID:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const saveNoticiaAcao = async (dadosNoticia, acaoId) => {
    isSaving.value = true;
    try {
      const response = await noticiaService.postNoticiaAcao(
        dadosNoticia,
        acaoId
      );
      return response;
    } catch (error) {
      console.error("Erro ao cadastrar notícia na ação:", error);
    } finally {
      isSaving.value = false;
    }
  };

  const fetchFiltrosNoticia = async ({
    titulo = "",
    statusId = "",
    abrangenciaId = "",
    acaoId = "",
    dataInicial = "",
    dataFinal = "",
    page = pagination.currentPage.value,
    size = pagination.pageSize.value,
    unidadeId = null,
  }) => {
    isLoading.value = true;
    try {
      const response = await noticiaService.filtrosNoticia({
        titulo,
        statusId,
        abrangenciaId,
        acaoId,
        dataInicial,
        dataFinal,
        page,
        size,
        unidadeId,
      });
      if (response && response.data) {
        noticias.value = response.data.content || response.data || [];
        pagination.setPaginationInfo(response.data);
        pagination.currentPage.value = page;
      }
      return response;
    } catch (error) {
      console.error("Erro ao filtrar notícias:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const updateNoticia = async (dadosNoticia, noticiaId) => {
    isSaving.value = true;
    try {
      const response = await noticiaService.putNoticia(dadosNoticia, noticiaId);
      return response;
    } catch (error) {
      console.error("Erro ao atualizar notícia:", error);
    } finally {
      isSaving.value = false;
    }
  };

  const deleteNoticia = async (noticiaId) => {
    isLoading.value = true;
    try {
      const response = await noticiaService.excluirNoticia(noticiaId);
      return response;
    } catch (error) {
      console.error("Erro ao excluir notícia:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchNoticiasByAbrangencia = async (
    page = pagination.currentPage.value,
    size = pagination.pageSize.value,
    abrangenciaId
  ) => {
    isLoading.value = true;
    try {
      const response = await noticiaService.getNoticiasByAbrangencia(
        page,
        size,
        abrangenciaId,
        true
      );
      if (response) {
        noticias.value = response.noticias || [];
        pagination.setPaginationInfo(response);
        pagination.currentPage.value = page;
      }
      return response;
    } catch (error) {
      console.error("Erro ao buscar notícias por abrangência:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchNoticiasByUnidade = async (
    page = pagination.currentPage.value,
    size = pagination.pageSize.value,
    unidadeId
  ) => {
    isLoading.value = true;
    try {
      const response = await noticiaService.getNoticiasByUnidade(
        page,
        size,
        unidadeId,
        true
      );
      if (response) {
        noticias.value = response.noticias || [];
        pagination.setPaginationInfo(response);
        pagination.currentPage.value = page;
      }
      return response;
    } catch (error) {
      console.error("Erro ao buscar notícias por unidade:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchNoticiasByAbrangenciaAndStatus = async (
    page = pagination.currentPage.value,
    size = pagination.pageSize.value,
    abrangenciaId,
    isAutorizado
  ) => {
    isLoading.value = true;
    try {
      const response = await noticiaService.getNoticiasByAbrangenciaAndStatus(
        page,
        size,
        abrangenciaId,
        isAutorizado,
        true
      );
      if (response) {
        noticias.value = response.noticias || [];
        pagination.setPaginationInfo(response);
        pagination.currentPage.value = page;
      }
      return response;
    } catch (error) {
      console.error("Erro ao buscar notícias por abrangência e status:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const autorizarNoticia = async (
    noticiaId,
    autorizado,
    motivoRejeicao = null
  ) => {
    isSaving.value = true;
    try {
      const response = await noticiaService.autorizarNoticia(
        noticiaId,
        autorizado,
        motivoRejeicao
      );
      return response;
    } catch (error) {
      console.error("Erro ao autorizar notícia:", error);
      throw error;
    } finally {
      isSaving.value = false;
    }
  };

  const fetchAutorizarNoticia = async (
    page = pagination.currentPage.value,
    size = pagination.pageSize.value
  ) => {
    isLoading.value = true;
    try {
      const response = await noticiaService.getAutorizarNoticia(page, size);
      pagination.setPaginationInfo(response);
      pagination.currentPage.value = page;
      return response;
    } catch (error) {
      console.error("Erro ao buscar autorização de notícia:", error);
    } finally {
      isLoading.value = false;
    }
  };

  // Abrangência
  const fetchAllAbrangencias = async () => {
    isLoading.value = true;
    try {
      const response = await noticiaService.getAllAbrangenciasNoticia();
      abrangencias.value = response || [];
      return response;
    } catch (error) {
      console.error("Erro ao listar abrangências:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchAbrangenciaById = async (abrangenciaId) => {
    isLoading.value = true;
    try {
      const response =
        await noticiaService.getAbrangenciaNoticiaById(abrangenciaId);
      abrangencia.value = response;
      return response;
    } catch (error) {
      console.error("Erro ao buscar abrangência por ID:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchAbrangenciaByName = async (nome) => {
    isLoading.value = true;
    try {
      const response = await noticiaService.getAbrangenciaNoticiaByName(nome);
      return response;
    } catch (error) {
      console.error("Erro ao buscar abrangência por nome:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const saveAbrangencia = async (dadosAbrangencia) => {
    isSaving.value = true;
    try {
      const response =
        await noticiaService.postAbrangenciaNoticia(dadosAbrangencia);
      return response;
    } catch (error) {
      console.error("Erro ao cadastrar abrangência:", error);
    } finally {
      isSaving.value = false;
    }
  };

  // Autores
  const fetchAllAutores = async () => {
    isLoading.value = true;
    try {
      const response = await noticiaService.getAllAutorNoticia();
      autores.value = response || [];
      return response;
    } catch (error) {
      console.error("Erro ao listar autores:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchAutorById = async (autorNoticiaId) => {
    isLoading.value = true;
    try {
      const response = await noticiaService.getAutorNoticiaById(autorNoticiaId);
      autor.value = response;
      return response;
    } catch (error) {
      console.error("Erro ao buscar autor por ID:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchAutorByName = async (nome) => {
    isLoading.value = true;
    try {
      const response = await noticiaService.getAutorNoticiaByName(nome);
      return response;
    } catch (error) {
      console.error("Erro ao buscar autor por nome:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const saveAutor = async (dadosAutor) => {
    isSaving.value = true;
    try {
      const response = await noticiaService.postAutorNoticia(dadosAutor);
      return response;
    } catch (error) {
      console.error("Erro ao cadastrar autor:", error);
    } finally {
      isSaving.value = false;
    }
  };

  // Status
  const fetchAllStatus = async () => {
    isLoading.value = true;
    try {
      const response = await noticiaService.getAllStatusNoticia();
      statusList.value = response || [];
      return response;
    } catch (error) {
      console.error("Erro ao listar status de notícias:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchStatusById = async (statusNoticiaId) => {
    isLoading.value = true;
    try {
      const response =
        await noticiaService.getStatusNoticiaById(statusNoticiaId);
      statusItem.value = response;
      return response;
    } catch (error) {
      console.error("Erro ao buscar status por ID:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchStatusByName = async (nome) => {
    isLoading.value = true;
    try {
      const response = await noticiaService.getStatusNoticiaByName(nome);
      return response;
    } catch (error) {
      console.error("Erro ao buscar status por nome:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const saveStatus = async (dadosStatus) => {
    isSaving.value = true;
    try {
      const response = await noticiaService.postStatusNoticia(dadosStatus);
      return response;
    } catch (error) {
      console.error("Erro ao cadastrar status de notícia:", error);
    } finally {
      isSaving.value = false;
    }
  };

  // Destaques
  const fetchDestaques = async () => {
    isLoading.value = true;
    try {
      const response = await noticiaService.getDestaques();
      destaques.value = response || [];
      return response;
    } catch (error) {
      console.error("Erro ao buscar destaques:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const saveDestaque = async (dadosDestaque) => {
    isSaving.value = true;
    try {
      const response = await noticiaService.postDestaque(dadosDestaque);
      return response;
    } catch (error) {
      console.error("Erro ao cadastrar destaque:", error);
      throw error;
    } finally {
      isSaving.value = false;
    }
  };

  const deleteDestaque = async (id) => {
    isLoading.value = true;
    try {
      const response = await noticiaService.excluirDestaque(id);
      return response;
    } catch (error) {
      console.error("Erro ao excluir destaque:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const reordenarDestaques = async (dadosOrdenacao) => {
    isSaving.value = true;
    try {
      const response = await noticiaService.reordenarDestaques(dadosOrdenacao);
      return response;
    } catch (error) {
      console.error("Erro ao reordenar destaques:", error);
      throw error;
    } finally {
      isSaving.value = false;
    }
  };

  return {
    noticias,
    noticia,
    destaques,
    abrangencias,
    abrangencia,
    autores,
    autor,
    statusList,
    statusItem,
    isLoading,
    isLoadingNoticia: isLoading,
    isSaving,
    isSavingNoticia: isSaving,
    currentPage: pagination.currentPage,
    totalPages: pagination.totalPages,
    pageSize: pagination.pageSize,
    hasNextPage: pagination.hasNextPage,
    hasPreviousPage: pagination.hasPreviousPage,
    fetchNoticiasHome,
    fetchAllNoticias,
    fetchNoticiasAcao,
    fetchNoticiaById,
    saveNoticiaAcao,
    fetchFiltrosNoticia,
    updateNoticia,
    deleteNoticia,
    fetchNoticiasByAbrangencia,
    fetchNoticiasByUnidade,
    fetchNoticiasByAbrangenciaAndStatus,
    autorizarNoticia,
    fetchAutorizarNoticia,
    fetchAllAbrangencias,
    fetchAbrangenciaById,
    fetchAbrangenciaByName,
    saveAbrangencia,
    fetchAllAutores,
    fetchAutorById,
    fetchAutorByName,
    saveAutor,
    fetchAllStatus,
    fetchStatusById,
    fetchStatusByName,
    saveStatus,
    fetchDestaques,
    saveDestaque,
    deleteDestaque,
    reordenarDestaques,
    nextPage: pagination.nextPage,
    previousPage: pagination.previousPage,
    goToPage: pagination.goToPage,
  };
}
